import React, { useState, useEffect } from 'react';
import { Modal, Box, Typography, TextField, Button, Grid, MenuItem } from '@mui/material';
import axios from 'axios';
import { API_PATH } from '../../../App';
import { getAxiosConfig } from '../../../helpers/AuthHelper';
import { AddMachineModalProps, Machine } from './types';


export default function AddMachineModal({
    open,
    handleClose,
    handleSubmit,
    selectedData,
}: AddMachineModalProps) {
    const [modelName, setModelName] = useState<string>('');
    const [vendorId, setVendorId] = useState<number | ''>('');
    const [coinName, setCoinName] = useState<string>('');
    const [hashRate, setHashRate] = useState<number>(0);
    const [hashUnit, setHashUnit] = useState<string>('');
    const [power, setPower] = useState<number>(0);
    const [price, setPrice] = useState<number>(0);
    const [hkPrice, setHkPrice] = useState<number>(0);
    const [predictionStatus, setPredictionStatus] = useState<boolean>(true);
    const [inventoryStatus, setInventoryStatus] = useState<string>('IN_STOCK');
    const [preOrderAvailableDate, setPreOrderAvailableDate] = useState<string>('');
    const [imageUrl, setImageUrl] = useState<string>('');
    const [videoUrl, setVideoUrl] = useState<string>('');
    const [setupType, setSetupType] = useState<string>('AIR');
    const [allowsFractional, setAllowsFractional] = useState<boolean>(false);
    const [minUnitHashRate, setMinUnitHashRate] = useState<number>(0);
    const [maxHashRate, setMaxHashRate] = useState<number>(0);


    const [vendors, setVendors] = useState<{ id: number; vendorName: string }[]>([]);
    const [coins, setCoins] = useState<{ coin: string; algorithm: string }[]>([]);

    useEffect(() => {
        axios.get(API_PATH + '/machine_model/vendors', getAxiosConfig()).then((response) => setVendors(response.data));
        axios.get(API_PATH + '/machine_model/currencies', getAxiosConfig()).then((response) => setCoins(response.data));
    }, []);

    useEffect(() => {
        if (selectedData) {
            setModelName(selectedData.modelName);
            setVendorId(selectedData.vendor.id);
            setCoinName(selectedData.coin.coin);
            setHashRate(selectedData.hashRate);
            setHashUnit(selectedData.hashUnit);
            setPower(selectedData.power);
            setPrice(selectedData.price)
            setHkPrice(selectedData.hkPrice);
            setPredictionStatus(selectedData.predictionStatus);
            setInventoryStatus(selectedData.inventoryStatus || 'IN_STOCK');
            setPreOrderAvailableDate(selectedData.preOrderAvailableDate?.split('T')[0] || '');
            setImageUrl(selectedData.imageUrl || '');
            setVideoUrl(selectedData.videoUrl || '')
            setSetupType(selectedData.setupType || 'AIR');
            setAllowsFractional(selectedData.allowsFractional || false);
            setMinUnitHashRate(selectedData.minUnitHashRate || 0);
            setMaxHashRate(selectedData.maxHashRate || 0);
        }
    }, [selectedData]);

    const handleSave = () => {
        const selectedVendor = vendors.find((v) => v.id === vendorId);
        const selectedCoin = coins.find((c) => c.coin === coinName);

        if (!selectedVendor || !selectedCoin) return;

        const newMachine: Machine = {
            id: selectedData?.id || null,
            modelName,
            vendor: selectedVendor,
            coin: selectedCoin,
            hashRate,
            hashUnit,
            isActive: true,
            price,
            power,
            hkPrice: hkPrice,
            predictionStatus: predictionStatus,
            inventoryStatus: inventoryStatus,
            preOrderAvailableDate: preOrderAvailableDate || null,
            imageUrl: imageUrl,
            videoUrl: videoUrl,
            setupType: setupType,
            allowsFractional: allowsFractional,
            minUnitHashRate: allowsFractional && minUnitHashRate > 0 ? minUnitHashRate : null,
            maxHashRate: allowsFractional && maxHashRate > 0 ? maxHashRate : null,
        };

        handleSubmit(newMachine);
    };
    const resetForm = () => {
        setModelName('');
        setVendorId('');
        setCoinName('');
        setHashRate(0);
        setHashUnit('');
        setPower(0);
        setPrice(0);
        setHkPrice(0);
        setPredictionStatus(true);
        setInventoryStatus('IN_STOCK');
        setPreOrderAvailableDate('');
        setImageUrl('');
        setVideoUrl('');
        setSetupType('AIR');
        setAllowsFractional(false);
        setMinUnitHashRate(0);
        setMaxHashRate(0);
    };

    useEffect(() => {
        if (!open) {
            resetForm();
        }
    }, [open]);

    return (
        <Modal open={open} onClose={handleClose}>
            <Box sx={{ 
                ...modalStyle,
                maxHeight: '90vh',
                overflow: 'auto',
                '&::-webkit-scrollbar': {
                    width: '8px',
                },
                '&::-webkit-scrollbar-thumb': {
                    backgroundColor: 'rgba(0,0,0,0.2)',
                    borderRadius: '4px',
                }
            }}>
                <Typography variant="h6" gutterBottom>
                    {selectedData ? 'Edit Machine' : 'Add Machine'}
                </Typography>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Model Name"
                            value={modelName}
                            onChange={(e) => setModelName(e.target.value)}
                            InputProps={{
                                readOnly: !!selectedData,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label="Vendor"
                            value={vendorId}
                            onChange={(e) => setVendorId(Number(e.target.value))}
                            InputProps={{
                                readOnly: !!selectedData,
                            }}
                        >
                            {vendors.map((vendor) => (
                                <MenuItem key={vendor.id} value={vendor.id}>
                                    {vendor.vendorName}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            select
                            fullWidth
                            label="Coin"
                            value={coinName}
                            onChange={(e) => setCoinName(e.target.value)}
                            InputProps={{
                                readOnly: !!selectedData,
                            }}
                        >
                            {coins.map((coin) => (
                                <MenuItem key={coin.coin} value={coin.coin}>
                                    {coin.coin} - {coin.algorithm}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            fullWidth
                            type="number"
                            label="Hash Rate"
                            value={hashRate === 0 ? '' : hashRate}
                            onChange={(e) => setHashRate(parseFloat(e.target.value) || 0)}
                            inputProps={{
                                step: 0.01,
                                min: 0
                            }}
                            InputProps={{
                                readOnly: !!selectedData,
                            }}
                        />
                    </Grid>
                    <Grid item xs={6}>
                        <TextField
                            select
                            fullWidth
                            label="Hash Unit"
                            value={hashUnit}
                            onChange={(e) => setHashUnit(e.target.value)}
                            InputProps={{
                                readOnly: !!selectedData,
                            }}
                        >
                            {['MH', 'GH', 'TH', 'PH'].map((unit) => (
                                <MenuItem key={unit} value={unit}>
                                    {unit}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Power (W)"
                            value={power === 0 ? '' : power}
                            onChange={(e) => setPower(Number(e.target.value))}
                            InputProps={{
                                readOnly: !!selectedData,
                            }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Price"
                            value={price === 0 ? '' : price}
                            onChange={(e) => setPrice(Number(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="HK Price"
                            value={Number(hkPrice) === 0 ? '' : Number(hkPrice)}
                            onChange={(e) => setHkPrice(Number(e.target.value))}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Image URL"
                            value={imageUrl}
                            onChange={(e) => setImageUrl(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Video URL"
                            value={videoUrl}
                            onChange={(e) => setVideoUrl(e.target.value)}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Setup Type"
                            select
                            value={setupType || ''}
                            onChange={(e) => setSetupType(e.target.value)}
                        >
                            <MenuItem value="">None</MenuItem>
                            <MenuItem value="AIR">AIR</MenuItem>
                            <MenuItem value="HYDRO">HYDRO</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Prediction Status"
                            select
                            value={predictionStatus ? 'true' : 'false'}
                            onChange={(e) => setPredictionStatus(e.target.value === 'true')}
                        >
                            <MenuItem value="true">True</MenuItem>
                            <MenuItem value="false">False</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Inventory Status"
                            select
                            value={inventoryStatus}
                            onChange={(e) => setInventoryStatus(e.target.value)}
                        >
                            <MenuItem value="IN_STOCK">In Stock</MenuItem>
                            <MenuItem value="OUT_OF_STOCK">Out of Stock</MenuItem>
                            <MenuItem value="PRE_ORDER">Pre Order</MenuItem>
                        </TextField>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Pre-Order Available Date"
                            type="date"
                            value={preOrderAvailableDate}
                            onChange={(e) => setPreOrderAvailableDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                        />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Fractional (Synthetic) Sales"
                            select
                            value={allowsFractional ? 'true' : 'false'}
                            onChange={(e) => setAllowsFractional(e.target.value === 'true')}
                        >
                            <MenuItem value="false">Not Allowed</MenuItem>
                            <MenuItem value="true">Allowed</MenuItem>
                        </TextField>
                    </Grid>
                    {allowsFractional && (
                        <>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={`Min Unit (${hashUnit || 'hash'})`}
                                    value={minUnitHashRate === 0 ? '' : minUnitHashRate}
                                    onChange={(e) => setMinUnitHashRate(parseFloat(e.target.value) || 0)}
                                    inputProps={{ step: 0.01, min: 0 }}
                                    helperText="Smallest sellable unit"
                                />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField
                                    fullWidth
                                    type="number"
                                    label={`Max per Machine (${hashUnit || 'hash'})`}
                                    value={maxHashRate === 0 ? '' : maxHashRate}
                                    onChange={(e) => setMaxHashRate(parseFloat(e.target.value) || 0)}
                                    inputProps={{ step: 0.01, min: 0 }}
                                    helperText="Cap for one synthetic machine"
                                />
                            </Grid>
                        </>
                    )}
                    <Grid item xs={12}>
                        <Button variant="contained" onClick={handleSave}>
                            {selectedData ? 'Update' : 'Add'} Machine
                        </Button>
                    </Grid>
                </Grid>
            </Box>

        </Modal>
    );
}

const modalStyle = {
    position: 'absolute' as 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: 400,
    bgcolor: 'background.paper',
    boxShadow: 24,
    p: 4,
};
